// ========================================
// useCheckIns Hook
// ========================================
// Custom hook for managing feelings check-ins

import { useState, useEffect } from 'react';
import { useAuth } from '../components/Auth/AuthContext';
import { getCheckIns, createCheckIn, getRecentCheckIn } from '../firebase/firestore';
import { CheckIn } from '../types';

export const useCheckIns = () => {
  const { currentUser } = useAuth();
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [recentCheckIn, setRecentCheckIn] = useState<CheckIn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load check-ins
  useEffect(() => {
    if (!currentUser) {
      setCheckIns([]);
      setLoading(false);
      return;
    }

    const loadCheckIns = async () => {
      try {
        setLoading(true);
        const data = await getCheckIns(currentUser.uid);
        setCheckIns(data);

        const recent = await getRecentCheckIn(currentUser.uid);
        setRecentCheckIn(recent);

        setError(null);
      } catch (err) {
        console.error('Error loading check-ins:', err);
        setError('Failed to load check-ins');
      } finally {
        setLoading(false);
      }
    };

    loadCheckIns();
  }, [currentUser]);

  // Add new check-in
  const addCheckIn = async (checkInData: Omit<CheckIn, 'id' | 'timestamp'>) => {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    try {
      const result = await createCheckIn(currentUser.uid, checkInData);

      // Refresh check-ins list
      const updatedCheckIns = await getCheckIns(currentUser.uid);
      setCheckIns(updatedCheckIns);

      const recent = await getRecentCheckIn(currentUser.uid);
      setRecentCheckIn(recent);

      return result;
    } catch (err) {
      console.error('Error adding check-in:', err);
      throw err;
    }
  };

  return {
    checkIns,
    recentCheckIn,
    loading,
    error,
    addCheckIn,
  };
};
